/**
 * Chrome's `innerHTML` serializer can drop or empty `img src` values that use
 * a custom scheme. TipTap `getHTML()` goes through that path, so restore each
 * image's document attr before the form persists.
 */
export function restoreImageSrcsFromAttrs(
  html: string,
  srcs: readonly string[],
): string {
  if (srcs.length === 0) {
    return html;
  }

  let index = 0;
  const withRestoredSrcs = html.replace(/<img\b[^>]*>/gi, tag => {
    const src = srcs[index++];
    if (!src) {
      return tag;
    }
    if (/\bsrc=/i.test(tag)) {
      return tag.replace(
        /\bsrc=(["'])[\s\S]*?\1/i,
        (_full, quote: string) => `src=${quote}${src}${quote}`,
      );
    }
    return `<img src="${src}"${tag.slice(4)}`;
  });

  const imgTagCount = html.match(/<img\b/gi)?.length ?? 0;
  if (imgTagCount >= srcs.length) {
    return withRestoredSrcs;
  }

  const missing = srcs.slice(imgTagCount);
  const tags = missing
    .map(src => `<img src="${src}" alt="attachment">`)
    .join('');
  if (!withRestoredSrcs.trim() || withRestoredSrcs === '<p></p>') {
    return `<p>${tags}</p>`;
  }
  if (withRestoredSrcs.endsWith('</p>')) {
    return `${withRestoredSrcs.slice(0, -4)}${tags}</p>`;
  }
  return `${withRestoredSrcs}${tags}`;
}

export function collectImageSrcAttrs(doc: {
  descendants: (
    fn: (node: {
      type: { name: string };
      attrs: Record<string, unknown>;
    }) => boolean | void,
  ) => void;
}): string[] {
  const srcs: string[] = [];
  doc.descendants(node => {
    if (
      node.type.name === 'image' &&
      typeof node.attrs.src === 'string' &&
      node.attrs.src.length > 0
    ) {
      srcs.push(node.attrs.src);
    }
  });
  return srcs;
}
